import type { Summary } from './types';
import { getChartRuntime } from './chartRuntime';
// Import region keywords to keep mapping consistent with data pipeline
// esbuild supports JSON imports at bundle time
// @ts-ignore
import regions from '../data/regions.json';

export const CATEGORY_COLORS: Record<string, string> = {
  Canada: '#EF4444',
  US: '#3B82F6',
  International: '#22C55E',
  Remote: '#94A3B8',
};

function stripCoop(label: string): string {
  if (!label) return '';
  const separators = ['·', ':'];
  for (const sep of separators) {
    if (label.includes(sep)) {
      const parts = label.split(sep);
      return (parts[1] ?? parts[0]).trim();
    }
  }
  return label.trim();
}
export function getSankeyCategory(locationOrLabel: string): string {
  const loc = stripCoop(locationOrLabel).trim().toLowerCase();
  if (!loc) return 'International';
  if ((regions as any)?.remote?.some((k: string) => k && loc.includes(k))) return 'Remote';
  if ((regions as any)?.canada?.some((k: string) => k && loc.includes(k))) return 'Canada';
  if ((regions as any)?.us?.some((k: string) => k && loc.includes(k))) return 'US';
  return 'International';
}

export function getSankeyColor(locationOrLabel: string): string {
  const cat = getSankeyCategory(locationOrLabel);
  return CATEGORY_COLORS[cat] ?? '#94A3B8';
}

export interface RenderSankeyOptions {
  colorMode?: 'to' | 'from' | 'gradient';
  collapseCoopPrefix?: boolean;
}
export function renderSankey(summary: Summary, opts?: RenderSankeyOptions, chartRuntime?: any): HTMLElement {
  const options: Required<RenderSankeyOptions> = {
    colorMode: opts?.colorMode ?? 'to',
    collapseCoopPrefix: opts?.collapseCoopPrefix ?? false,
  };

  const container = document.createElement('div');
  container.className = 'chart chart--sankey';

  const linksData = summary.links ?? [];
  if (!linksData.length) {
    container.innerHTML = '<div class="empty-state">No flow data available.</div>';
    return container;
  }

  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  const ChartRuntime = chartRuntime ?? getChartRuntime();

  const flowData = linksData.map((link) => ({
    from: link.source,
    to: link.target,
    flow: link.value,
  }));

  const nodeKeys = new Set<string>(summary.nodes ?? []);
  if (!nodeKeys.size) {
    flowData.forEach((d) => { nodeKeys.add(d.from); nodeKeys.add(d.to); });
  }

  // Compute flow totals per node (max of in/out to handle intermediate nodes)
  const nodeIn = new Map<string, number>();
  const nodeOut = new Map<string, number>();
  flowData.forEach((d) => {
    nodeOut.set(d.from, (nodeOut.get(d.from) ?? 0) + d.flow);
    nodeIn.set(d.to, (nodeIn.get(d.to) ?? 0) + d.flow);
  });
  const nodeFlow = (key: string) => Math.max(nodeIn.get(key) ?? 0, nodeOut.get(key) ?? 0);

  // Group nodes by term prefix (e.g. "Co-op 1") and compute term totals
  const termTotals = new Map<string, number>();
  nodeKeys.forEach((key) => {
    const termMatch = key.match(/^(Co-op\s*\d+)/i);
    const term = termMatch ? termMatch[1] : key;
    termTotals.set(term, (termTotals.get(term) ?? 0) + nodeFlow(key));
  });

  // Node labels: dataset.labels is an object map { [nodeKey]: displayLabel }
  const labelsMap: Record<string, string> = {};
  nodeKeys.forEach((key) => {
    const base = options.collapseCoopPrefix ? stripCoop(key) : key;
    const termMatch = key.match(/^(Co-op\s*\d+)/i);
    const term = termMatch ? termMatch[1] : key;
    const total = termTotals.get(term) ?? 0;
    const flow = nodeFlow(key);
    const pct = total > 0 ? Math.round((flow / total) * 100) : 0;
    labelsMap[key] = `${base} (${pct}%)`;
  });

  const dataset = {
    label: 'Flow',
    data: flowData,
    // Scriptable per-element colors — the only way the plugin resolves per-flow color
    colorFrom: (ctx: any) => getSankeyColor(stripCoop(ctx.raw?.from ?? '')),
    colorTo: (ctx: any) => getSankeyColor(stripCoop(ctx.raw?.to ?? '')),
    borderWidth: 0,
    labels: labelsMap,
    nodeWidth: 20,
    nodePadding: 100,
    align: 'justify',
  } as any;
  const chart = new ChartRuntime(canvas, {
    type: 'sankey',
    data: { datasets: [dataset] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        tooltip: {
          callbacks: {
            label(context: any) {
              const d = context.raw as { from: string; to: string; flow: number };
              const fromShort = stripCoop(d.from);
              const toShort = stripCoop(d.to);
              return `${fromShort} → ${toShort}: ${d.flow} respondents`;
            },
          },
        },
        legend: { display: false },
      },
    },
  });

  return container;
}
